import { c as _c } from "react/compiler-runtime";
import { DiagnosticShell } from '@src/components/sco/PitchDeck/PitchDeckLandingDiagnostic/DiagnosticShell';
import { RegisterStep } from '@src/components/sco/PitchDeck/PitchDeckLandingDiagnostic/RegisterStep';
import { UploadStep } from '@src/components/sco/PitchDeck/PitchDeckLandingDiagnostic/UploadStep';
import { PITCH_DECK_LANDING_LEGACY_TEASER_STEPS } from '@src/components/sco/PitchDeck/PitchDeckLandingDiagnostic/types';
import { RequestFunnelContextProvider, useRequestFunnelContext } from '@src/components/sco/RequestFunnel/RequestFunnelContext';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
export type PitchDeckLandingDiagnosticProps = {
  funnelIdentifier?: string;
};

/** Imperative API for mid-page CTAs that open the diagnostic as a modal. */
export type LandingDiagnosticHandle = {
  open: () => void;
};
const PitchDeckLandingDiagnosticInner = forwardRef<LandingDiagnosticHandle>(function PitchDeckLandingDiagnosticInner(_props, ref) {
  const $ = _c(21);
  const {
    step,
    setStep
  } = useRequestFunnelContext();
  const rawStep = step || "start";
  let t0;
  if ($[0] !== rawStep) {
    t0 = (PITCH_DECK_LANDING_LEGACY_TEASER_STEPS as readonly string[]).includes(rawStep);
    $[0] = rawStep;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isLegacyTeaser = t0;
  const activeStep = isLegacyTeaser ? "register" : rawStep;
  const [forceModal, setForceModal] = useState(false);
  const focused = forceModal || activeStep !== "start";
  const showBack = activeStep === "register";
  let t1;
  let t2;
  if ($[2] !== isLegacyTeaser || $[3] !== setStep) {
    t1 = () => {
      if (isLegacyTeaser) {
        setStep("register");
      }
    };
    t2 = [isLegacyTeaser, setStep];
    $[2] = isLegacyTeaser;
    $[3] = setStep;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => ({
      open: () => {
        setForceModal(true);
      }
    });
    t4 = [];
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useImperativeHandle(ref, t3, t4);
  let t5;
  if ($[8] !== activeStep || $[9] !== setStep) {
    t5 = () => {
      if (activeStep === "register") {
        setStep("start");
      }
    };
    $[8] = activeStep;
    $[9] = setStep;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const onBack = t5;
  let t6;
  if ($[11] !== setStep) {
    t6 = () => {
      setForceModal(false);
      setStep("start");
    };
    $[11] = setStep;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const onDismiss = t6;
  let t7;
  if ($[13] !== activeStep) {
    t7 = activeStep === "register" ? <RegisterStep /> : <UploadStep />;
    $[13] = activeStep;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== focused || $[16] !== onBack || $[17] !== onDismiss || $[18] !== showBack || $[19] !== t7) {
    t8 = <DiagnosticShell focused={focused} showBack={showBack} onBack={onBack} onDismiss={onDismiss}>{t7}</DiagnosticShell>;
    $[15] = focused;
    $[16] = onBack;
    $[17] = onDismiss;
    $[18] = showBack;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
});

/**
 * Compact inline pitch deck audit diagnostic for the Pitch Deck LP hero.
 * Persists the uploaded deck on SignupRequest. Logged-in users skip register
 * after upload: commit the signup request and open `/pitch-decks/{id}`. Guests
 * enter email/register, then the same commit into the auditor.
 *
 * Call `ref.open()` from mid-page CTAs to present the same diagnostic as a modal.
 */
export const PitchDeckLandingDiagnostic = forwardRef<LandingDiagnosticHandle, PitchDeckLandingDiagnosticProps>(function PitchDeckLandingDiagnostic(t0, ref) {
  const $ = _c(8);
  const {
    funnelIdentifier: t1
  } = t0;
  const funnelIdentifier = t1 === undefined ? "pitch-deck-landing-diagnostic" : t1;
  let t2;
  if ($[0] !== funnelIdentifier) {
    t2 = {
      groupRequestTypeKey: "funding",
      funnelIdentifier
    };
    $[0] = funnelIdentifier;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== ref) {
    t3 = <PitchDeckLandingDiagnosticInner ref={ref} />;
    $[2] = ref;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== funnelIdentifier || $[5] !== t2 || $[6] !== t3) {
    t4 = <RequestFunnelContextProvider funnelIdentifier={funnelIdentifier} saveOnStepChange={true} initialSignupRequest={t2}>{t3}</RequestFunnelContextProvider>;
    $[4] = funnelIdentifier;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
});
export default PitchDeckLandingDiagnostic;